// Dependency-free browser ZIP writer (STORE method, no compression).
// It keeps the PACS portal package-free while producing a standard .zip file.

const encoder = new TextEncoder();

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let value = n;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[n] = value >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  for (let index = 0; index < bytes.length; index += 1) {
    crc = CRC_TABLE[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const dosDateTime = (input) => {
  const date = input instanceof Date ? input : new Date(input || Date.now());
  const safeYear = Math.max(1980, date.getFullYear());
  return {
    time:
      ((date.getHours() & 0x1f) << 11) |
      ((date.getMinutes() & 0x3f) << 5) |
      ((Math.floor(date.getSeconds() / 2) || 0) & 0x1f),
    date:
      (((safeYear - 1980) & 0x7f) << 9) |
      (((date.getMonth() + 1) & 0x0f) << 5) |
      (date.getDate() & 0x1f),
  };
};

const toBytes = async (value) => {
  if (value instanceof Uint8Array) return value;
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (ArrayBuffer.isView(value))
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  if (value instanceof Blob) return new Uint8Array(await value.arrayBuffer());
  return encoder.encode(String(value ?? ""));
};

const writeUint16 = (view, offset, value) =>
  view.setUint16(offset, value & 0xffff, true);

const writeUint32 = (view, offset, value) =>
  view.setUint32(offset, value >>> 0, true);

const normalizeZipPath = (value, index) => {
  const path = String(value || `file_${index + 1}`)
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")
    .replace(/\.{2,}/g, ".")
    .replace(/\/{2,}/g, "/");
  return path || `file_${index + 1}`;
};

export async function createZipBlob(entries = []) {
  if (!Array.isArray(entries) || !entries.length) {
    throw new Error("At least one file is required to create a ZIP package.");
  }

  const prepared = [];
  let localSize = 0;

  for (let index = 0; index < entries.length; index += 1) {
    const entry = entries[index] || {};
    const name = normalizeZipPath(entry.name, index);
    const nameBytes = encoder.encode(name);
    const data = await toBytes(entry.data);
    const stamp = dosDateTime(entry.lastModified);
    const record = {
      name,
      nameBytes,
      data,
      crc: crc32(data),
      time: stamp.time,
      date: stamp.date,
      offset: localSize,
    };
    localSize += 30 + nameBytes.length + data.length;
    prepared.push(record);
  }

  const centralSize = prepared.reduce(
    (total, entry) => total + 46 + entry.nameBytes.length,
    0,
  );
  const output = new Uint8Array(localSize + centralSize + 22);
  const view = new DataView(output.buffer);
  let cursor = 0;

  prepared.forEach((entry) => {
    writeUint32(view, cursor, 0x04034b50);
    writeUint16(view, cursor + 4, 20);
    writeUint16(view, cursor + 6, 0x0800); // UTF-8 filename
    writeUint16(view, cursor + 8, 0); // STORE
    writeUint16(view, cursor + 10, entry.time);
    writeUint16(view, cursor + 12, entry.date);
    writeUint32(view, cursor + 14, entry.crc);
    writeUint32(view, cursor + 18, entry.data.length);
    writeUint32(view, cursor + 22, entry.data.length);
    writeUint16(view, cursor + 26, entry.nameBytes.length);
    writeUint16(view, cursor + 28, 0);
    output.set(entry.nameBytes, cursor + 30);
    output.set(entry.data, cursor + 30 + entry.nameBytes.length);
    cursor += 30 + entry.nameBytes.length + entry.data.length;
  });

  const centralOffset = cursor;
  prepared.forEach((entry) => {
    writeUint32(view, cursor, 0x02014b50);
    writeUint16(view, cursor + 4, 20);
    writeUint16(view, cursor + 6, 20);
    writeUint16(view, cursor + 8, 0x0800);
    writeUint16(view, cursor + 10, 0);
    writeUint16(view, cursor + 12, entry.time);
    writeUint16(view, cursor + 14, entry.date);
    writeUint32(view, cursor + 16, entry.crc);
    writeUint32(view, cursor + 20, entry.data.length);
    writeUint32(view, cursor + 24, entry.data.length);
    writeUint16(view, cursor + 28, entry.nameBytes.length);
    writeUint16(view, cursor + 30, 0);
    writeUint16(view, cursor + 32, 0);
    writeUint16(view, cursor + 34, 0);
    writeUint16(view, cursor + 36, 0);
    writeUint32(view, cursor + 38, 0);
    writeUint32(view, cursor + 42, entry.offset);
    output.set(entry.nameBytes, cursor + 46);
    cursor += 46 + entry.nameBytes.length;
  });

  writeUint32(view, cursor, 0x06054b50);
  writeUint16(view, cursor + 4, 0);
  writeUint16(view, cursor + 6, 0);
  writeUint16(view, cursor + 8, prepared.length);
  writeUint16(view, cursor + 10, prepared.length);
  writeUint32(view, cursor + 12, cursor - centralOffset);
  writeUint32(view, cursor + 16, centralOffset);
  writeUint16(view, cursor + 20, 0);

  return new Blob([output], { type: "application/zip" });
}

export function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = String(fileName || "PACS_Documents.zip");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export async function downloadZip(entries, fileName) {
  const blob = await createZipBlob(entries);
  downloadBlob(blob, fileName);
  return blob;
}

export async function fetchRemoteBlob(url, label = "document") {
  if (!url) throw new Error(`${label} link is missing.`);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`${label} could not be downloaded (${response.status}).`);
  }
  return response.blob();
}

